import styled from 'styled-components/macro';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { BaseReactComponent, SelectOption } from '../../../types';

import { HiTooltip, HiBadge, HiChip, HiInput, HiMenuItem } from '..';
import { Autocomplete, AutocompleteProps, useFormControl } from '@mui/material';

import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteValue
} from '@mui/base/AutocompleteUnstyled/useAutocomplete';

export interface HiAutocompleteProps<
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends BaseReactComponent {
  options: ReadonlyArray<SelectOption>;
  value: AutocompleteValue<string, Multiple, false, FreeSolo>;
  onChange: (
    value: string | string[] | null,
    optionValue: AutocompleteValue<SelectOption, Multiple, false, FreeSolo>,
    event: React.SyntheticEvent,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<SelectOption> | undefined
  ) => void;
  onInputChange?: (
    value: string,
    event: React.SyntheticEvent,
    reason: AutocompleteInputChangeReason
  ) => void;
  disabled?: AutocompleteProps<
    SelectOption,
    Multiple,
    false,
    FreeSolo
  >['disabled'];
  placeholder?: string;
  multiple?: Multiple;
  freeSolo?: FreeSolo;
  loading?: boolean;
  async?: boolean;
}

// When multiple, max number of tags display
const LIMIT_TAGS = 2;

const HiAutocomplete = <
  Multiple extends boolean | undefined,
  FreeSolo extends boolean | undefined
>({
  options,
  value,
  onChange,
  onInputChange,
  disabled,
  placeholder,
  multiple,
  freeSolo,
  loading,
  async = false,
  ...other
}: HiAutocompleteProps<Multiple, FreeSolo>) => {
  const intl = useIntl();
  const formControl = useFormControl();

  // Map options to key/value object to be able to request labels from id
  // Used to convert value array for Autocomplete format
  const _optionsMapping = useMemo<Record<string, SelectOption>>(() => {
    const mapping: Record<string, SelectOption> = {};
    options.forEach((o) => {
      mapping[o.id] = o;
    });
    return mapping;
  }, [options]);

  // Compute value to Autocomplete format (transform (list of) string to (list of) SelectOption)
  const _value = useMemo<
    AutocompleteValue<SelectOption, Multiple, false, FreeSolo>
  >(() => {
    let computedValue;
    // If multiple
    if (Array.isArray(value)) {
      computedValue = value.map((v) => {
        return { id: v, label: _optionsMapping[v]?.label ?? v };
      });
    } else {
      // value is null when field empty
      if (value === null) {
        computedValue = null;
      } else {
        computedValue = {
          id: value,
          label: _optionsMapping[value as string]?.label ?? value
        };
      }
    }
    // prettier-ignore
    return computedValue as AutocompleteValue<SelectOption, Multiple, false, FreeSolo>;
  }, [_optionsMapping, value]);

  // Handle Autocomplete value change
  const handleChange = (
    event: React.SyntheticEvent,
    val: AutocompleteValue<SelectOption, Multiple, false, FreeSolo>,
    reason: AutocompleteChangeReason,
    details: AutocompleteChangeDetails<SelectOption> | undefined
  ) => {
    let _onChangeValue;
    // Transform val from (list of) SelectOption to (list of) string
    if (Array.isArray(val)) {
      _onChangeValue = val.map((v) => (v as SelectOption).id ?? v);
    } else {
      _onChangeValue = (val as SelectOption)?.id ?? val;
    }
    onChange && onChange(_onChangeValue, val, event, reason, details);
  };

  // Handle Autocomplete Input value change
  // Used for async freeSolo search for example
  const handleInputChange = (
    event: React.SyntheticEvent,
    val: string,
    reason: AutocompleteInputChangeReason
  ) => {
    onInputChange && onInputChange(val, event, reason);
  };

  return (
    <Autocomplete<SelectOption, Multiple, false, FreeSolo>
      {...other}
      loading={loading}
      options={options}
      filterOptions={async ? (x) => x : undefined}
      value={_value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={disabled || formControl?.disabled}
      disableCloseOnSelect={multiple}
      multiple={multiple}
      freeSolo={freeSolo}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option, state) => {
        return (
          <HiMenuItem
            {...props}
            {...option}
            onClick={props.onClick}
            checkbox={multiple}
            selected={state.selected}
            disableMenuItemSelected
          />
        );
      }}
      renderTags={(values, getTagProps, ownerState) => {
        const limitTags = ownerState.limitTags || LIMIT_TAGS;

        return (
          <>
            {values
              .slice(0, limitTags)
              .map((option: SelectOption, index: number) => {
                const {
                  key: chipKey,
                  className: chipClassName,
                  onDelete: chipOnAction,
                  ...chipProps
                } = getTagProps({ index });
                return (
                  <HiChip
                    key={chipKey}
                    className={chipClassName}
                    type="removable"
                    size="small"
                    label={option.label}
                    onAction={chipOnAction}
                    ChipProps={chipProps}
                  />
                );
              })}
            {values.length > limitTags && (
              <HiBadge
                className="HiAutocomplete-more-tags-badge"
                color="info"
                content={
                  <HiTooltip
                    title={values
                      .slice(2)
                      .map((v) => v.label)
                      .join(', ')}
                    sx={{ mb: '4px !important' }}
                  >
                    <span>+{values.length - limitTags}</span>
                  </HiTooltip>
                }
              />
            )}
          </>
        );
      }}
      renderInput={(params) => {
        return (
          <HiInput
            // Remove HiInput clear icon and use Autocomplete one
            clearIcon={false}
            InputProps={{
              ...params.InputProps,
              inputProps: { ...params.inputProps },
              disabled: params.disabled,
              fullWidth: true
            }}
            placeholder={placeholder}
          />
        );
      }}
      noOptionsText={intl.formatMessage({ id: 'HiAutocomplete.noOptions' })}
      loadingText={intl.formatMessage({ id: 'HiAutocomplete.loading' })}
      closeText={intl.formatMessage({ id: 'HiAutocomplete.close' })}
      openText={intl.formatMessage({ id: 'HiAutocomplete.open' })}
      clearText={intl.formatMessage({ id: 'HiInput.clear' })}
      classes={{
        endAdornment: 'HiAutocomplete-endAdornment',
        tag: 'HiAutocomplete-tag'
      }}
    />
  );
};

const StyledHiAutocomplete = styled(HiAutocomplete)`
  min-width: 226px;

  .HiAutocomplete-endAdornment {
    pointer-events: auto;

    button {
      margin-right: 0;
    }
  }

  .HiAutocomplete-tag {
    pointer-events: auto;
    max-width: 85px !important;
    box-shadow: ${({ theme }) => theme.shadows[2]};
  }

  .HiAutocomplete-more-tags-badge {
    cursor: default;
    pointer-events: auto;
    margin: 3px;
  }
`;

export default StyledHiAutocomplete;
